import React from 'react';
import { SaveOutlined } from '@ant-design/icons';
import { Card, Checkbox, Col, Form, Input, message, Modal, Radio, Row, Spin, Tabs } from 'antd';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { useTranslation } from 'react-i18next';
import { TransportRoleType } from '@/constants';
import { getFullName } from '@/helpers/string';
import { BusAutocompletionModel, TripAssignmentModel, TripDetailCalendarDto, UserBasicInfoModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';
import SeatingInfoTab from './components/SeatingInfoTab';
import MapTab from './components/MapTab';

type Props = {
  onClose: () => void;
  trip: TripDetailCalendarDto;
  transportDrivers: UserBasicInfoModel[];
  buses: BusAutocompletionModel[];
};

export default function TripInfoModal({ onClose, trip, transportDrivers, buses }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [tripAssignment, setTripAssignment] = React.useState<TripAssignmentModel>();
  const [matchBusModelOnly, setMatchBusModelOnly] = React.useState(true);
  const [detailOnly, setDetailOnly] = React.useState<boolean>(false);
  const canUpdate = useCanManageFleetTrip();
  const [activeTab, setActiveTab] = React.useState('tab1');
  const fetchData = () => {
    setLoading(true);
    apiSdk.TripDetailService.getTripAssignment({
      tripDetailId: trip.id || '',
    })
      .then(res => {
        setTripAssignment(res);
        form.setFieldsValue(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (trip.id) {
      fetchData();
    }
  }, [trip]);

  const onSave = () => {
    form
      .validateFields()
      .then(values => {
        setLoading(true);
        apiSdk.TripDetailService.updateTripAssignment({
          body: {
            busId: values.busId,
            coDriverId: values.coDriverId,
            driverId: values.driverId,
            id: trip.id,
          },
        })
          .then(response => {
            if (response?.success) {
              setDetailOnly(true);
              fetchData();
            } else {
              if (response.message) {
                message.error(t(response.message || ''));
                setLoading(false);
              }
            }
          })
          .catch(error => {
            message.error(error);
            setLoading(false);
          });
      })
      .catch(info => {
        setActiveTab('tab1');
      });
  };
  const { drivers, coDrivers } = React.useMemo(() => {
    return {
      drivers: transportDrivers
        .filter(x => x.roles?.includes(TransportRoleType.Driver))
        .map(x => ({
          value: x.id || '',
          label: getFullName(x.firstName, x.lastName),
        })),
      coDrivers: transportDrivers
        .filter(x => x.roles?.includes(TransportRoleType.CoDriver))
        .map(x => ({
          value: x.id || '',
          label: getFullName(x.firstName, x.lastName),
        })),
    };
  }, [transportDrivers]);

  const busOptions = React.useMemo(() => {
    if (matchBusModelOnly) {
      return buses.filter(x => x.seatingModelId === tripAssignment?.seatingModelId);
    }
    return buses;
  }, [buses, matchBusModelOnly, tripAssignment]);

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.extraLarge}
      className="nta-custom-form"
      title={`${tripAssignment?.tripRefNo}`}
      forceRender={true}
      style={{ top: 10 }}
      okText={t('save')}
      cancelText={detailOnly ? t('Close') : t('cancel')}
      okButtonProps={{
        icon: <SaveOutlined />,
        className: 'save-btn',
        loading: loading,
        style: { display: detailOnly || !canUpdate ? 'none' : undefined },
      }}
      onCancel={onClose}
      onOk={onSave}
    >
      <Tabs activeKey={activeTab} onTabClick={t => setActiveTab(t)}>
        <Tabs.TabPane tab="Trip info" key="tab1">
          <Spin spinning={loading}>
            <Form form={form} layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
              <Row gutter={ROW_GUTTER}>
                <Col md={12}>
                  <Form.Item label={t('tripDetailsPage_TripRefNumber')}>
                    <Input value={tripAssignment?.tripRefNo} className="bold input-value-color" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripDetailsPage_SourceStation')}>
                    <Input value={tripAssignment?.sourceStation} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripDetailsPage_DestinationStation')}>
                    <Input value={tripAssignment?.destinationStation} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_NumberOfPassenger')}>
                    <Input value={tripAssignment?.numberOfPassenger} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_NoOfSeats')}>
                    <Input value="N/A" className="input-value-color bold" disabled />
                  </Form.Item>
                </Col>
                <Col md={12}>
                  <Form.Item label={t('tripsCalendarPage_Driver')}>
                    <Input value={tripAssignment?.driver} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_CoDriver')}>
                    <Input value={tripAssignment?.coDriver} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_BusName')}>
                    <Input value={tripAssignment?.busName} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_BusPlate')}>
                    <Input value={tripAssignment?.busPlateNumber} className="input-value-color bold" disabled />
                  </Form.Item>
                  <Form.Item label={t('tripsCalendarPage_NoOfConflictedBooking')}>
                    <Input value={tripAssignment?.numberOfConflictedTicket} className="input-value-color bold" disabled />
                  </Form.Item>
                </Col>
              </Row>
              {!detailOnly ? (
                <Row gutter={ROW_GUTTER}>
                  <Col md={12}>
                    <Card size="small" className="mtopo-card" title={t('tripsCalendarPage_Driver')}>
                      <Form.Item name="driverId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Radio.Group options={drivers} className="radio-option-single-line" />
                      </Form.Item>
                    </Card>
                    <Card size="small" className="mtopo-card" title={t('tripsCalendarPage_CoDriver')}>
                      <Form.Item name="coDriverId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Radio.Group options={coDrivers} className="radio-option-single-line" />
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card
                      size="small"
                      className="mtopo-card"
                      title={t('Bus')}
                      extra={
                        <Checkbox
                          checked={matchBusModelOnly}
                          onClick={() => {
                            setMatchBusModelOnly(x => !x);
                          }}
                        >
                          <span style={{ color: 'white' }}>{t('tripsCalendarPage_OnlyShowSameBusModel')}</span>
                        </Checkbox>
                      }
                    >
                      <div style={{ display: 'flex', marginLeft: 25, marginRight: 8, fontWeight: 'bold', alignItems: 'center', marginBottom: 5 }}>
                        <div style={{ flex: 1, textAlign: 'left' }}>{t('tripsCalendarPage_BusName')}</div>
                        <div style={{ width: 120, textAlign: 'right' }}>{t('tripsCalendarPage_BusPlate')}</div>
                        <div style={{ width: 150, textAlign: 'right' }}>{t('tripsCalendarPage_BusBrand')}</div>
                      </div>
                      <Form.Item name="busId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Radio.Group className="radio-option-single-line label-w-100">
                          {busOptions.map(bus => {
                            return (
                              <Radio
                                key={bus.id}
                                value={bus.id}
                                style={{ color: bus.seatingModelId === tripAssignment?.seatingModelId ? '#5F63F2' : undefined }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <div style={{ flex: 1, textAlign: 'left' }}>{bus.name}</div>
                                  <div style={{ width: 120, textAlign: 'right' }}>{bus.plateNumber}</div>
                                  <div style={{ width: 150, textAlign: 'right' }}>{bus.busBrand}</div>
                                </div>
                              </Radio>
                            );
                          })}
                        </Radio.Group>
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              ) : null}
            </Form>
          </Spin>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Seat info & passengers" key="tab2">
          <SeatingInfoTab trip={trip} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Map" key="tab3">
          <MapTab trip={trip} />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
}
